import { type FunctionComponent, type ReactElement, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { CloseButton, TitleButtonWrapper } from './CloseButton'
import Close from '../../../assets/icons/close.svg'
import FocusLock from '../../FocusLock'
import Heading from '../../Heading/Heading'

const focusableElements = [
  'a[href]:not([disabled])',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
]

const ModalContainer = styled.div`
  position: absolute;
`

const BackDrop = styled.div`
  inset: 0;
  display: block;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2000;
`

const Container = styled.div`
  position: fixed;
  background-color: var(--light);
  overflow: auto;
  width: 100%;
  max-width: 100%;
  max-height: 75%;
  top: 5.75rem;
  left: 50%;
  transform: translate(-50%, 0);
  pointer-events: all;
  z-index: 2001;
  padding: 1.5rem 1rem;

  @media screen and (min-width: 48rem) {
    max-width: 620px;
    max-height: 75%;
    padding: 2.5rem;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`
interface ModalProps {
  children: ReactElement | ReactElement[]
  open: boolean
  hideCloseButton?: boolean
  title?: string
  smallTitle?: boolean
  onClose?: () => void
  className?: string
}
const Modal: FunctionComponent<ModalProps> = ({
  children,
  open,
  title,
  smallTitle,
  hideCloseButton,
  onClose,
  className,
  ...otherProps
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const previouslyFocusedElement = useMemo(() => document.activeElement, [open])
  const handleClose = () => {
    if (onClose) {
      if (previouslyFocusedElement instanceof HTMLElement) {
        previouslyFocusedElement.focus()
      }
      onClose()
    }
  }

  useEffect(() => {
    if (!open || !ref.current) {
      return
    }

    const firstFocusableEl = ref.current.querySelector(focusableElements.join(', '))

    if (firstFocusableEl instanceof HTMLElement) {
      firstFocusableEl.focus()
    }
  }, [open])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (onClose && event?.key === 'Escape') {
        event.stopPropagation()
        handleClose()
      }
    }

    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [onClose])

  if (open) {
    return (
      <FocusLock isLocked={open}>
        <ModalContainer {...otherProps}>
          <BackDrop onClick={onClose} />
          <Container
            aria-labelledby="dialog_label"
            role="dialog"
            aria-modal="true"
            className={className}
            ref={ref}
          >
            <TitleBlock>
              {title &&
                (smallTitle ? (
                  <Heading type="h4" id="dialog_label">
                    {title}
                  </Heading>
                ) : (
                  <Heading type="h3" large id="dialog_label">
                    {title}
                  </Heading>
                ))}
              {!hideCloseButton && (
                <TitleButtonWrapper>
                  <CloseButton title="Sluit" type="button" onClick={onClose} small={smallTitle}>
                    <Close />
                  </CloseButton>
                </TitleButtonWrapper>
              )}
            </TitleBlock>
            {children}
          </Container>
        </ModalContainer>
      </FocusLock>
    )
  }
  return null
}

export default Modal
